import { readFileSync } from 'fs';
import { Recorder, StreamingConfig } from '../types/voice.types';
import { resolve } from 'path';
import WebSocket from 'isomorphic-ws';

interface InitiateResponse {
  id: string;
  url: string;
}

const gladiaApiUrl = 'https://api.gladia.io';
const gladiaKey = import.meta.env.FARM_VOICE_API;

export const config: StreamingConfig = {
  encoding: 'wav/pcm',
  sample_rate: 48000,
  bit_depth: 16,
  channels: 1,
};

export function printMessage(message: {
  type: 'transcript' | 'post_final_transcript';
  data: any;
}) {
  if (message.type === 'transcript' && message.data.is_final) {
    const { text, start, end } = message.data.utterance;
    console.log(
      `${formatSeconds(start)} --> ${formatSeconds(end)} | ${text.trim()}`
    );
  } else if (message.type === 'post_final_transcript') {
    console.log();
    console.log('################ End of session ################');
    console.log();
    console.log(JSON.stringify(message.data, null, 2));
  }
}

function extractDurationFromDurationInMs(durationInMs: number) {
  if (!Number.isFinite(durationInMs) || durationInMs < 0) {
    throw new Error(`${durationInMs} isn't a valid duration`);
  }

  const milliseconds = Math.floor(durationInMs % 1000);
  let seconds = Math.floor(durationInMs / 1000);
  let minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  seconds = seconds % 60;
  minutes = minutes % 60;

  return {
    hours,
    minutes,
    seconds,
    milliseconds,
  };
}

function formatSeconds(duration: number | null | undefined) {
  if (
    duration == null ||
    Number.isNaN(duration) ||
    !Number.isFinite(duration)
  ) {
    return '--:--.---';
  }
  const { hours, minutes, seconds, milliseconds } =
    extractDurationFromDurationInMs(duration * 1000);
  const fractions: number[] = [minutes, seconds];
  if (hours) fractions.unshift(hours);
  return [
    fractions.map((number) => number.toString().padStart(2, '0')).join(':'),
    milliseconds.toString().padStart(3, '0'),
  ].join('.');
}

export function initFileRecorder(
  config: StreamingConfig,
  onAudioChunk: (chunk: Buffer) => void,
  onEnd: () => void,
  filePath = '../data/anna-and-sasha-16000.wav'
): Recorder {
  const chunkDuration = 0.1; // 100 ms
  const bytesPerSample = config.bit_depth / 8;
  const bytesPerSecond = config.sample_rate * config.channels * bytesPerSample;
  const chunkSize = chunkDuration * bytesPerSecond;

  const buffer = readFileSync(resolve(filePath))
    // remove wav header
    .subarray(44);

  const recorder = {
    interval: null as NodeJS.Timeout | null,
    start() {
      let offset = 0;
      this.interval = setInterval(() => {
        onAudioChunk(buffer.subarray(offset, (offset += chunkSize)));

        if (offset >= buffer.length) {
          console.log('>>>>> Sent all audio data');
          this.stop();
          onEnd();
        }
      }, chunkDuration * 1000);
    },
    stop() {
      if (!this.interval) return;

      // Ending the recording

      clearInterval(this.interval);
      this.interval = null;

      console.log('>>>>> Ending the recording…');
      onEnd();
    },
  };

  // When hitting CTRL+C, we want to stop the recording and get the final result
  process.on('SIGINT', () => recorder.stop());

  return recorder;
}

const WS_URL_KEY = 'voice_ws_url';

export const getStoredWebSocketUrl = (): string | null => {
  return localStorage.getItem(WS_URL_KEY);
};

export const storeWebSocketUrl = (url: string): void => {
  localStorage.setItem(WS_URL_KEY, url);
};

export const clearStoredWebSocketUrl = (): void => {
  localStorage.removeItem(WS_URL_KEY);
};

export const closeExistingWebSocket = async (): Promise<void> => {
  const existingUrl = getStoredWebSocketUrl();
  if (existingUrl) {
    try {
      const socket = new WebSocket(existingUrl);
      await new Promise<void>((resolve, reject) => {
        socket.onopen = () => {
          console.log('Closing existing WebSocket connection...');
          socket.close();
          clearStoredWebSocketUrl();
          resolve();
        };
        socket.onerror = () => {
          console.log(
            'Error connecting to existing WebSocket, clearing URL...'
          );
          clearStoredWebSocketUrl();
          resolve();
        };
        // Timeout after 5 seconds
        setTimeout(() => {
          clearStoredWebSocketUrl();
          resolve();
        }, 5000);
      });
    } catch (error) {
      console.error('Error closing existing WebSocket:', error);
      clearStoredWebSocketUrl();
    }
  }
};

export const initLiveSession = async (): Promise<InitiateResponse> => {
  await closeExistingWebSocket();

  const response = await fetch(`${gladiaApiUrl}/v2/live`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-GLADIA-KEY': gladiaKey || '',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  storeWebSocketUrl(data.url);
  return data;
};

export const initWebSocketClient = (
  url: string,
  onMessage: (text: string) => void
): WebSocket => {
  const socket = new WebSocket(url);

  socket.onopen = () => {
    console.log('Connected to websocket');
  };

  socket.onmessage = (event: MessageEvent) => {
    const message = JSON.parse(event.data);
    if (message.type === 'transcript' && message.data.is_final) {
      onMessage(message.data.utterance.text);
    }
  };

  socket.onerror = (error: Error) => {
    console.error('WebSocket error:', error);
  };

  socket.onclose = () => {
    console.log('WebSocket closed');
  };

  return socket;
};
